import type { FieldErrors } from 'react-hook-form';
import type { TFunction } from 'i18next';
import type { AllowedModelsResponse } from '@circuitry-ai/doc-data';
import type { EditAgentFormValues } from './voiceAgentEditFormTypes';

const err = (message: string) => ({ type: 'validate' as const, message });

function recordToOption(r: Record<string, string> | undefined): { value: string; label: string } | null {
  if (!r || typeof r !== 'object') return null;
  const entries = Object.entries(r);
  if (entries.length === 0) return null;
  const [label, value] = entries[0];
  return { value: value ?? '', label: label ?? '' };
}

function recordToOptions(r: Record<string, string> | undefined): Array<{ value: string; label: string }> {
  if (!r || typeof r !== 'object') return [];
  return Object.entries(r).map(([label, value]) => ({ label, value: value ?? '' }));
}

function ttsModelOptionCountForProvider(
  allowed: AllowedModelsResponse | undefined,
  provider: string | undefined
): number {
  if (!provider || !allowed?.allowed_models?.tts) return 0;
  const entry = allowed.allowed_models.tts.find((e) => recordToOption(e.provider)?.value === provider);
  if (!entry) return 0;
  return recordToOptions(entry.model_id ?? entry.model).length;
}

export interface VoiceAgentEditValidationContext {
  outboundEnabled: boolean;
  inboundEnabled: boolean;
  allowedModels: AllowedModelsResponse | undefined;
}

function nonEmpty(s: string | undefined | null): boolean {
  return typeof s === 'string' && s.trim().length > 0;
}

/**
 * Client-side checks aligned with AgentConfig OpenAPI (discriminated first_message, minLength on speak_first.text, etc.).
 */
export function validateVoiceAgentEditForm(
  values: EditAgentFormValues,
  ctx: VoiceAgentEditValidationContext,
  t: TFunction
): FieldErrors<EditAgentFormValues> {
  const errors: FieldErrors<EditAgentFormValues> = {};
  const callHandling: 'outbound' | 'inbound' | 'both' =
    ctx.outboundEnabled && ctx.inboundEnabled ? 'both' : ctx.inboundEnabled ? 'inbound' : 'outbound';

  const llm = values.llm;
  const llmErrors: NonNullable<FieldErrors<EditAgentFormValues>['llm']> = {};
  if (!nonEmpty(llm?.provider)) {
    llmErrors.provider = err(t('Select an LLM provider.'));
  }
  if (nonEmpty(llm?.provider) && !nonEmpty(llm?.model)) {
    llmErrors.model = err(t('Select an LLM model for this provider.'));
  }
  if (llm?.temperature != null && (typeof llm.temperature !== 'number' || !Number.isFinite(llm.temperature))) {
    llmErrors.temperature = err(t('Enter a valid temperature (number).'));
  }
  if (callHandling === 'outbound' || callHandling === 'both') {
    if (!nonEmpty(llm?.system_prompt_template_outbound)) {
      llmErrors.system_prompt_template_outbound = err(t('Enter the outbound system prompt.'));
    }
  }
  if (callHandling === 'inbound' || callHandling === 'both') {
    if (!nonEmpty(llm?.system_prompt_template)) {
      llmErrors.system_prompt_template = err(t('Enter the inbound system prompt.'));
    }
  }
  if (Object.keys(llmErrors).length > 0) {
    errors.llm = llmErrors;
  }

  const stt = values.stt;
  const sttErrors: NonNullable<FieldErrors<EditAgentFormValues>['stt']> = {};
  if (!nonEmpty(stt?.provider)) {
    sttErrors.provider = err(t('Select a speech-to-text provider.'));
  }
  const sttModel = stt?.live_options?.model ?? stt?.model;
  if (nonEmpty(stt?.provider) && !nonEmpty(sttModel)) {
    sttErrors.live_options = { model: err(t('Select a speech-to-text model for this provider.')) };
  }
  if (Object.keys(sttErrors).length > 0) {
    errors.stt = sttErrors;
  }

  const tts = values.tts;
  const ttsErrors: NonNullable<FieldErrors<EditAgentFormValues>['tts']> = {};
  if (!nonEmpty(tts?.provider)) {
    ttsErrors.provider = err(t('Select a text-to-speech provider.'));
  } else if (tts.provider !== 'dynamic' && !nonEmpty(tts?.voice_id)) {
    ttsErrors.voice_id = err(t('Select a voice for this provider.'));
  }
  const ttsModelVal = tts?.model_id ?? tts?.model;
  const ttsModelCatalogCount = ttsModelOptionCountForProvider(ctx.allowedModels, tts?.provider);
  if (
    nonEmpty(tts?.provider) &&
    tts?.provider !== 'dynamic' &&
    tts?.provider !== 'azure' &&
    ttsModelCatalogCount > 0 &&
    !nonEmpty(ttsModelVal)
  ) {
    ttsErrors.model_id = err(t('Select a TTS model for this provider.'));
  }
  if (Object.keys(ttsErrors).length > 0) {
    errors.tts = ttsErrors;
  }

  const validModes = new Set(['speak_first', 'wait_for_user']);

  if (ctx.inboundEnabled) {
    const fm = values.first_message;
    const mode = fm?.mode ?? '';
    const fmErrors: NonNullable<FieldErrors<EditAgentFormValues>['first_message']> = {};
    if (!nonEmpty(mode) || !validModes.has(mode)) {
      fmErrors.mode = err(t('Select a first-message mode for inbound.'));
    } else if (mode === 'speak_first' && !nonEmpty(fm?.text)) {
      fmErrors.text = err(t('Enter the inbound opening line (required when the assistant speaks first).'));
    }
    if (Object.keys(fmErrors).length > 0) {
      errors.first_message = fmErrors;
    }
  }

  if (ctx.outboundEnabled) {
    const fo = values.first_message_outbound;
    const mode = fo?.mode ?? '';
    const foErrors: NonNullable<FieldErrors<EditAgentFormValues>['first_message_outbound']> = {};
    if (!nonEmpty(mode) || !validModes.has(mode)) {
      foErrors.mode = err(t('Select a first-message mode for outbound.'));
    } else if (mode === 'speak_first' && !nonEmpty(fo?.text)) {
      foErrors.text = err(t('Enter the outbound opening line (required when the assistant speaks first).'));
    }
    if (Object.keys(foErrors).length > 0) {
      errors.first_message_outbound = foErrors;
    }
  }

  return errors;
}
